import { CdkVirtualScrollViewport, ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DateTime } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import {
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncSelectionCondition,
  SelectionMode,
} from '@mode-vegas/vegas';
import { IconComponent, SearchInputComponent, TooltipDirective, trackByFactory } from '@mode/capra';
import { ChartDate } from '@mode/charts/util-vegas';
import { ReportFilterDomainResult } from '@mode/filters/util';
import { ReportTypes } from '@mode/shared/contract-common';
import { formatInUTC, generateUUID } from '@mode/shared/util-js';
import { CompiledField } from '@mode/shared/util-switch';
import { BehaviorSubject, Subject, debounceTime, filter, takeUntil } from 'rxjs';

export const SELECT_ALL_VALUE = 'øselect-allø';

interface FilterValueItem {
  value: number | string | boolean | null;
  label: string;
  selected: boolean;
}

@Component({
  selector: 'mode-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, SearchInputComponent, ScrollingModule, IconComponent, TooltipDirective],
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MultiSelectComponent implements OnChanges, OnDestroy, OnInit {
  @Input() filter!: EncDiscreteFilterField | EncDiscreteBooleanFilterField;
  @Input() filterOption!: ReportFilterDomainResult; // TODO: figure out how to support unformatted options
  @Input() compiledField: CompiledField | null = null;
  @Input() isLoading = true;
  @Input() showAll = true;
  @Input() isOpen = false;
  @Input() sortSelectedFirst = true;

  @ViewChild(CdkVirtualScrollViewport, { static: false })
  cdkVirtualScrollViewPort: CdkVirtualScrollViewport | undefined;

  @Output() filterChange = new EventEmitter<{
    filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField;
    selectedValue: ReportTypes.ReportFilterValue;
    isChecked: boolean;
    searchTerm: string;
  }>();

  @ViewChild('selectAllInput') selectAllInput: ElementRef | undefined;

  trackByValue = trackByFactory<FilterValueItem>(({ value }) => value);

  readonly itemHeight = 24;

  searchTerm = '';
  filteredValueList: Array<FilterValueItem> = [];
  unfilteredValueList: Array<FilterValueItem> = [];

  isAll = false;
  hasPartialSelection = false;
  isExclude = false;
  filterDescription = 'Loading filter values';
  uniqueId = '';
  minimumContainerHeight = `${this.itemHeight * 2}px`;

  visibleFilterValues$ = new BehaviorSubject<Array<FilterValueItem>>([]);
  searchTerm$ = new BehaviorSubject('');
  isLoadingSubject$ = new BehaviorSubject<boolean>(true);
  private destroyed$ = new Subject<void>();

  ngOnInit(): void {
    this.searchTerm$
      .pipe(
        takeUntil(this.destroyed$),
        filter((searchTerm) => searchTerm != null),
        debounceTime(400)
      )
      .subscribe((searchTerm: string) => {
        this.searchTerm = searchTerm;

        const lowercasedTerm = searchTerm.toLowerCase();
        this.filteredValueList = this.unfilteredValueList.filter((filterValue) =>
          filterValue.label.toLowerCase().includes(lowercasedTerm)
        );

        this.visibleFilterValues$.next(this.filteredValueList);
      });

    this.uniqueId = generateUUID().replace(/-/g, '');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filter?.currentValue) {
      const currentSelection = this.filter.selection;
      this.isAll = currentSelection?.mode === SelectionMode.ALL || currentSelection?.mode === SelectionMode.EXCLUDE_ALL;
      this.isExclude =
        currentSelection?.mode === SelectionMode.EXCLUDE || currentSelection?.mode === SelectionMode.EXCLUDE_ALL;
      this.hasPartialSelection = !this.isAll && (currentSelection?.selected ?? []).length > 0;
      const selected = new Set(currentSelection.selected);
      this.unfilteredValueList.forEach((filterValue) => {
        filterValue.selected = this.isAll || selected.has(filterValue.value);
      });
    }

    if (changes.isOpen && this.cdkVirtualScrollViewPort) {
      this.cdkVirtualScrollViewPort.scrollToIndex(0);
      this.cdkVirtualScrollViewPort.checkViewportSize();
    }

    if (changes.filterOption?.currentValue) {
      this.buildValueList(this.filterOption);
      this.minimumContainerHeight = this.calculateContainerHeight();
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  onSelectAll() {
    const allStatesMatch = this.allSelectedStatesMatch(this.filteredValueList);
    let isChecked = false;

    if (allStatesMatch) {
      // Toggle the selection
      const isSelected = this.filteredValueList[0]?.selected ?? false;
      if (isSelected) {
        this.deselectAll();
      } else {
        this.selectAll();
        isChecked = true;
      }
    } else {
      // Mix of selected/unselected. Default behavior is to deselect all
      this.deselectAll();
    }

    /** @todo This could be problematic if a string value matches {@link SELECT_ALL_VALUE} */
    this.onSelectionChange(SELECT_ALL_VALUE, isChecked);

    if (this.selectAllInput?.nativeElement !== undefined) {
      // The checkbox makes the selection/deselection regardless of what
      // the model says. We need to make sure that it is correct.
      this.selectAllInput.nativeElement['checked'] = this.isAll;
    }
  }

  onSelectItem($event: Event, value: ReportTypes.ReportFilterValue) {
    const target = $event.target as HTMLInputElement;
    if (target) {
      this.onSelectionChange(value, target.checked);
    }
  }

  deselectAll() {
    this.filteredValueList.forEach((filterValue) => (filterValue.selected = false));
  }

  selectAll() {
    this.filteredValueList.forEach((filterValue) => (filterValue.selected = true));
  }

  onSelectionChange(selectedValue: ReportTypes.ReportFilterValue, isChecked: boolean) {
    const selectedList = this.unfilteredValueList.filter((filterValue) => filterValue.selected);
    const searchTerm = this.searchTerm;

    this.isAll = selectedList.length === this.unfilteredValueList.length;
    this.hasPartialSelection = !this.isAll && selectedList.length > 0;

    const selection = this.buildSelectionCondition();
    this.filter = {
      ...this.filter,
      selection,
    };

    this.setFilterDescription(selectedList.length, this.unfilteredValueList.length);
    this.filterChange.emit({
      filter: this.filter,
      selectedValue,
      isChecked,
      searchTerm,
    });
  }

  buildSelectionCondition(): EncSelectionCondition {
    if (this.isAll) {
      return {
        mode: this.isExclude ? SelectionMode.EXCLUDE_ALL : SelectionMode.ALL,
        selected: [],
      };
    }

    const selectedList = this.unfilteredValueList
      .filter((filterValue) => filterValue.selected)
      .map((filterValue) => filterValue.value);

    return {
      selected: selectedList,
      mode: this.isExclude ? SelectionMode.EXCLUDE : SelectionMode.INCLUDE,
    };
  }

  buildValueList(result: ReportFilterDomainResult | undefined) {
    const selectionCondition = this.filter.selection;
    if (result?.type === Attr.Type.Discrete) {
      const selectionSet = new Set(selectionCondition?.selected ?? []);
      const selected: FilterValueItem[] = [];
      const results: FilterValueItem[] = [];

      // Sorting shouldn't be necessary at the moment because Flamingo automatically applies a data sort to the results

      const labels = result.labels ?? [];
      result.values.forEach((value, index) => {
        const label = labels[index] || this.formatValue(value);
        if (this.sortSelectedFirst && selectionSet.has(value)) {
          selected.push({
            selected: true,
            value,
            label,
          });
        } else {
          results.push({
            selected: this.isAll || selectionSet.has(value),
            value,
            label,
          });
        }
      });

      const searchTerm = this.searchTerm.toLowerCase();
      this.unfilteredValueList = selected.concat(results);
      this.filteredValueList = this.unfilteredValueList.filter((filterValue) =>
        filterValue.label.toLowerCase().includes(searchTerm)
      );

      const selectedList = this.isAll ? this.unfilteredValueList : selected;
      this.setFilterDescription(selectedList.length, this.unfilteredValueList.length);
      this.visibleFilterValues$.next(this.filteredValueList);
    }
  }

  onSearchTermChanged(searchTerm: string) {
    this.searchTerm$.next(searchTerm);
  }

  formatValue(value: string | number | boolean | undefined | null) {
    if (value === null || value === undefined) {
      return 'Null';
    }
    if (typeof value === 'string') {
      return value;
    }

    // Only used for chart filters that have a comfiledField passed in
    // this is applicable to chart filters, but not report filters
    if ((this.compiledField && this.compiledField?.hasDateFunc) || this.compiledField?.isFormulaDate) {
      if (this.compiledField.hasDatePart) {
        switch (this.compiledField.dateFuncUnit) {
          case DateTime.Unit.Quarter:
            return ChartDate.makeDatePartQuarterName(value);
          case DateTime.Unit.Month:
            return ChartDate.makeDatePartMonthName(value);
          case DateTime.Unit.Weekday:
            return ChartDate.makeDatePartWeekdayName(value);
          default:
            return String(value);
        }
      }
      return formatInUTC(new Date(+value), 'yyyy-MM-dd HH:mm:ss');
    }
    return String(value);
  }

  // Per: https://stackoverflow.com/a/67089033
  calculateContainerHeight(): string {
    // Account for additional "Select All" option
    const totalItems = this.unfilteredValueList.length + (this.showAll ? 1 : 0);

    // The final number of items you want to keep visible (1-10, inclusive)
    const visibleItems = Math.min(Math.max(totalItems, 1), 10);
    return `${this.itemHeight * visibleItems}px`;
  }

  get isShowRelevantValuesOnly() {
    return this.filter?.showRelevantValuesOnly ?? false;
  }

  private setFilterDescription(numSelected: number, totalItems: number) {
    this.filterDescription =
      totalItems === 1 ? `${numSelected} of 1 item selected` : `${numSelected} of ${totalItems} items selected.`;
  }

  private allSelectedStatesMatch(items: Array<FilterValueItem>) {
    if (items.length === 0) {
      return true;
    }
    const firstState = items[0].selected;
    // Return false if any value is different from the first
    return !items.some((value) => value.selected !== firstState);
  }
}
